import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { OccEndpointsService } from '@spartacus/core';
import { GeocodeResult } from '@yandex-maps/const';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

const KILOMETERS_IN_EQUATOR_DEGREE = 111.3;

interface YandexGeocodeAddressComponent {
  kind: string;
  name: string;
}

interface YandexGeocodeAddress {
  country_code?: string;
  postal_code?: string;
  formatted?: string;
  Components?: YandexGeocodeAddressComponent[];
}

interface YandexGeocodeMetaData {
  kind?: string;
  text?: string;
  precision?: string;
  Address?: YandexGeocodeAddress;
}

interface YandexGeoObject {
  metaDataProperty: {
    GeocoderMetaData: YandexGeocodeMetaData;
  };
  description?: string;
  name?: string;
  boundedBy?: {
    Envelope: {
      lowerCorner: string;
      upperCorner: string;
    };
  };
  Point?: {
    pos: string;
  };
}

interface YandexGeocodeResponse {
  response: {
    GeoObjectCollection: {
      metaDataProperty: {
        GeocoderResponseMetaData: {
          request: string;
          found: string;
          results: string;
        };
      };
      featureMember: Array<{ GeoObject: YandexGeoObject }>;
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class GeocodeService {
  private readonly http = inject(HttpClient);
  private readonly occEndpointsService = inject(OccEndpointsService);

  geocode(addressOrCoords: string | ymaps.Coordinate): Observable<GeocodeResult> {
    const formattedStr = Array.isArray(addressOrCoords)
      ? `${addressOrCoords[1]},${addressOrCoords[0]}`
      : addressOrCoords;

    const baseUrl = this.occEndpointsService.getBaseUrl({ prefix: false, baseSite: false });

    return this.http
      .get<YandexGeocodeResponse>(`${baseUrl}/utils/geocode-maps?geocode=${formattedStr}&lang=ru_RU&format=json`)
      .pipe(map((res) => this.mapToGeocodeResult(res)));
  }

  private mapToGeocodeResult(response: YandexGeocodeResponse): GeocodeResult {
    const geoObject = response?.response?.GeoObjectCollection?.featureMember?.[0]?.GeoObject;
    const meta = geoObject?.metaDataProperty?.GeocoderMetaData;
    const address = meta?.Address;
    const components = address?.Components ?? [];

    const findComponent = (kind: string): string | null => components.find((c) => c.kind === kind)?.name ?? null;
    const findComponents = (kind: string): string[] => components.filter((c) => c.kind === kind).map((c) => c.name);

    const pos = geoObject?.Point?.pos?.split(' ').map(Number) ?? [0, 0];
    // pos приходит в порядке "долгота широта", в ymaps координаты — [широта, долгота]
    const coordinates: ymaps.Coordinate = [pos[1], pos[0]];

    const addressLine = meta?.text ?? address?.formatted ?? '';

    return {
      geometry: {
        getCoordinates: () => coordinates,
        getType: () => 'Point',
        getBounds: () => {
          if (!geoObject?.boundedBy) {
            return null;
          }
          const lower = geoObject.boundedBy.Envelope.lowerCorner.split(' ').map(Number);
          const upper = geoObject.boundedBy.Envelope.upperCorner.split(' ').map(Number);

          return [
            [lower[1], lower[0]],
            [upper[1], upper[0]],
          ] as ymaps.CoordinateBounds;
        },
      } as unknown as GeocodeResult['geometry'],
      properties: {
        get: (key: string) => {
          switch (key) {
            case 'name':
              return geoObject?.name;
            case 'description':
              return geoObject?.description;
            case 'text':
              return meta?.text;
            case 'metaDataProperty':
              return geoObject?.metaDataProperty;
            default:
              return undefined;
          }
        },
      } as unknown as ymaps.IGeoObject['properties'],
      options: {} as unknown as ymaps.IGeoObject['options'],
      state: {} as unknown as ymaps.IGeoObject['state'],
      getAddressLine: () => addressLine,
      getAdministrativeAreas: () => findComponents('province').slice(0, 2),
      getArea: () => findComponent('area'),
      getCountry: () => findComponent('country'),
      getCountryCode: () => address?.country_code ?? null,
      getLocalities: () => findComponents('locality'),
      getPremise: () => null,
      getPremiseNumber: () => findComponent('house'),
      getThoroughfare: () => findComponent('street'),
    } as GeocodeResult;
  }

  getGeoObject(addressOrCoords: string | ymaps.Coordinate): Observable<GeocodeResult> {
    return this.geocode(addressOrCoords);
  }

  getAddressByCoords(coords: ymaps.Coordinate): Observable<string> {
    return this.getGeoObject(coords).pipe(map((geoObject: GeocodeResult) => geoObject.getAddressLine()));
  }

  getDistanceInKm(a: ymaps.Coordinate, b: ymaps.Coordinate): number {
    return (
      Math.sqrt(Math.pow(a[0] - b[0], 2) + Math.pow((a[1] - b[1]) * Math.cos((a[0] * Math.PI) / 180), 2)) *
      KILOMETERS_IN_EQUATOR_DEGREE
    );
  }
}
